/**
 * Domain enums. These mirror the Prisma enums 1:1 — keep both in sync.
 * Declared as const objects + literal unions so they are usable as values in
 * the API and as types in the React app without importing Prisma client.
 */
export const AccountNature = {
    ASSET: "ASSET",
    LIABILITY: "LIABILITY",
    EQUITY: "EQUITY",
    INCOME: "INCOME",
    EXPENSE: "EXPENSE",
};
/** Which side increases a balance of this nature. */
export const NATURE_NORMAL_SIDE = {
    ASSET: "DEBIT",
    EXPENSE: "DEBIT",
    LIABILITY: "CREDIT",
    EQUITY: "CREDIT",
    INCOME: "CREDIT",
};
/** Where the group lands in the statements. */
export const StatementSection = {
    BALANCE_SHEET: "BALANCE_SHEET",
    PROFIT_AND_LOSS: "PROFIT_AND_LOSS",
};
export const DrCr = { DEBIT: "DEBIT", CREDIT: "CREDIT" };
export const LedgerType = {
    GENERAL: "GENERAL",
    CASH: "CASH",
    BANK: "BANK",
    CUSTOMER: "CUSTOMER",
    VENDOR: "VENDOR",
    CONTRACTOR: "CONTRACTOR",
    LANDOWNER: "LANDOWNER",
    TAX: "TAX",
    TDS: "TDS",
    STOCK: "STOCK",
    WIP: "WIP",
    FIXED_ASSET: "FIXED_ASSET",
    LOAN: "LOAN",
    CAPITAL: "CAPITAL",
    SUSPENSE: "SUSPENSE",
    ROUNDING: "ROUNDING",
};
/**
 * Voucher classes. The first eight are the classical Tally vouchers; the rest
 * are builder-specific business events. Every one of them ultimately posts a
 * balanced journal through the accounting engine — none of them touch ledger
 * balances directly.
 */
export const VoucherClass = {
    // Classical
    PAYMENT: "PAYMENT",
    RECEIPT: "RECEIPT",
    CONTRA: "CONTRA",
    JOURNAL: "JOURNAL",
    SALES: "SALES",
    PURCHASE: "PURCHASE",
    DEBIT_NOTE: "DEBIT_NOTE",
    CREDIT_NOTE: "CREDIT_NOTE",
    // Builder-specific
    CUSTOMER_BOOKING: "CUSTOMER_BOOKING",
    CUSTOMER_DEMAND: "CUSTOMER_DEMAND",
    CUSTOMER_RECEIPT: "CUSTOMER_RECEIPT",
    CUSTOMER_REFUND: "CUSTOMER_REFUND",
    CUSTOMER_CANCELLATION: "CUSTOMER_CANCELLATION",
    REGISTRATION: "REGISTRATION",
    POSSESSION: "POSSESSION",
    CONTRACTOR_BILL: "CONTRACTOR_BILL",
    CONTRACTOR_ADVANCE: "CONTRACTOR_ADVANCE",
    CONTRACTOR_RETENTION: "CONTRACTOR_RETENTION",
    MATERIAL_PURCHASE: "MATERIAL_PURCHASE",
    MATERIAL_ISSUE: "MATERIAL_ISSUE",
    PROJECT_EXPENSE: "PROJECT_EXPENSE",
    LANDOWNER_SETTLEMENT: "LANDOWNER_SETTLEMENT",
    PROJECT_TRANSFER: "PROJECT_TRANSFER",
    BANK_TRANSFER: "BANK_TRANSFER",
    // System
    OPENING_BALANCE: "OPENING_BALANCE",
    DEPRECIATION: "DEPRECIATION",
    WIP_CAPITALIZATION: "WIP_CAPITALIZATION",
    YEAR_END_CLOSING: "YEAR_END_CLOSING",
};
export const CLASSICAL_VOUCHER_CLASSES = [
    "PAYMENT",
    "RECEIPT",
    "CONTRA",
    "JOURNAL",
    "SALES",
    "PURCHASE",
    "DEBIT_NOTE",
    "CREDIT_NOTE",
];
/** Document lifecycle. Posted vouchers are immutable; corrections are reversals. */
export const VoucherStatus = {
    DRAFT: "DRAFT",
    SUBMITTED: "SUBMITTED",
    APPROVED: "APPROVED",
    POSTED: "POSTED",
    REVERSED: "REVERSED",
    CANCELLED: "CANCELLED",
};
export const PROJECT_STATUSES = [
    "PLANNING",
    "APPROVAL_PENDING",
    "LAUNCHED",
    "UNDER_CONSTRUCTION",
    "COMPLETED",
    "HANDED_OVER",
    "ON_HOLD",
    "CLOSED",
];
export const PROJECT_TYPES = [
    "RESIDENTIAL_APARTMENT",
    "VILLA",
    "PLOTTED_DEVELOPMENT",
    "COMMERCIAL",
    "MIXED_USE",
    "IT_PARK",
    "WAREHOUSE",
    "REDEVELOPMENT",
];
/**
 * Financial/project states of a unit. NOT a CRM pipeline — no lead stages here.
 */
export const UNIT_STATUSES = [
    "AVAILABLE",
    "HOLD",
    "BOOKED",
    "AGREEMENT",
    "REGISTERED",
    "CANCELLED",
    "POSSESSION",
    "SOLD",
];
/** Which unit states represent an inventory commitment against the developer. */
export const COMMITTED_UNIT_STATUSES = [
    "BOOKED",
    "AGREEMENT",
    "REGISTERED",
    "POSSESSION",
    "SOLD",
];
/**
 * Permitted unit-state moves. These are FINANCIAL states — a unit advances as
 * money and paperwork progress, and it can only fall back to AVAILABLE through
 * an explicit cancellation.
 *
 * The server enforces this; the client reads it so a screen offers only moves
 * that will actually be accepted, rather than presenting every status and
 * letting the user discover the rule through a rejection.
 */
export const UNIT_STATUS_FLOW = {
    AVAILABLE: ["HOLD", "BOOKED"],
    HOLD: ["AVAILABLE", "BOOKED"],
    BOOKED: ["AGREEMENT", "CANCELLED"],
    AGREEMENT: ["REGISTERED", "CANCELLED"],
    REGISTERED: ["POSSESSION", "CANCELLED"],
    POSSESSION: ["SOLD"],
    SOLD: [],
    CANCELLED: ["AVAILABLE"],
};
/**
 * States a person may not set directly, and why. A unit becomes BOOKED by
 * creating a booking; it returns to AVAILABLE by cancelling one. Money moves in
 * both cases, so the transition belongs to the booking, not to this dropdown.
 */
export const UNIT_STATUS_NOT_DIRECTLY_SETTABLE = {
    BOOKED: "A unit becomes booked by creating a booking, not by setting the status.",
};
export const UNIT_OWNERSHIP = ["DEVELOPER", "LANDOWNER", "JV_POOL"];
export const PARTY_TYPES = [
    "CUSTOMER",
    "VENDOR",
    "CONTRACTOR",
    "LANDOWNER",
    "EMPLOYEE",
    "BANK",
    "OTHER",
];
export const GST_REGISTRATION_TYPES = [
    "REGULAR",
    "COMPOSITION",
    "UNREGISTERED",
    "SEZ",
    "OVERSEAS",
    "CONSUMER",
];
export const TAX_TYPES = ["GST", "TDS", "TCS", "CESS", "NONE"];
export const GST_COMPONENTS = ["CGST", "SGST", "IGST", "CESS"];
export const TAX_TREATMENTS = [
    "EXCLUSIVE",
    "INCLUSIVE",
    "EXEMPT",
    "NIL_RATED",
    "ZERO_RATED",
    "REVERSE_CHARGE",
];
export const SCHEDULE_BASIS = [
    "PERCENTAGE",
    "FIXED_AMOUNT",
    "DATE_INSTALLMENT",
    "CONSTRUCTION_MILESTONE",
    "CUSTOM",
];
export const DEMAND_STATUS = [
    "PENDING",
    "RAISED",
    "PARTIALLY_PAID",
    "PAID",
    "OVERDUE",
    "WAIVED",
    "CANCELLED",
];
export const BOOKING_STATUS = [
    "ACTIVE",
    "CANCELLED",
    "TRANSFERRED",
    "COMPLETED",
];
export const PO_STATUS = [
    "DRAFT",
    "PENDING_APPROVAL",
    "APPROVED",
    "PARTIALLY_RECEIVED",
    "RECEIVED",
    "CLOSED",
    "CANCELLED",
];
export const WORK_ORDER_STATUS = [
    "DRAFT",
    "PENDING_APPROVAL",
    "APPROVED",
    "IN_PROGRESS",
    "COMPLETED",
    "CLOSED",
    "CANCELLED",
];
export const STOCK_MOVEMENT_TYPES = [
    "GRN_INWARD",
    "PURCHASE_RETURN",
    "ISSUE_TO_SITE",
    "RETURN_FROM_SITE",
    "TRANSFER_OUT",
    "TRANSFER_IN",
    "ADJUSTMENT_INCREASE",
    "ADJUSTMENT_DECREASE",
    "WASTAGE",
    "OPENING",
];
/** Movements that add to stock; everything else reduces it. */
export const INWARD_MOVEMENTS = [
    "GRN_INWARD",
    "RETURN_FROM_SITE",
    "TRANSFER_IN",
    "ADJUSTMENT_INCREASE",
    "OPENING",
];
export const APPROVAL_STATUS = [
    "PENDING",
    "APPROVED",
    "REJECTED",
    "SKIPPED",
    "WITHDRAWN",
];
export const BANK_RECON_STATUS = [
    "UNRECONCILED",
    "SUGGESTED",
    "RECONCILED",
    "IGNORED",
];
export const DEPRECIATION_METHODS = ["SLM", "WDV", "NONE"];
export const LOAN_TYPES = [
    "PROJECT_LOAN",
    "TERM_LOAN",
    "WORKING_CAPITAL",
    "OVERDRAFT",
    "UNSECURED",
    "VEHICLE",
    "OTHER",
];
export const DOCUMENT_ENTITY_TYPES = [
    "COMPANY",
    "PROJECT",
    "TOWER",
    "UNIT",
    "CUSTOMER",
    "BOOKING",
    "VENDOR",
    "CONTRACTOR",
    "LANDOWNER",
    "VOUCHER",
    "PURCHASE_ORDER",
    "WORK_ORDER",
    "RA_BILL",
    "GRN",
    "PAYMENT",
    "FIXED_ASSET",
    "LOAN",
    "JDA",
    "OTHER",
];
export const IMPORT_ENTITY_TYPES = [
    "LEDGERS",
    "OPENING_BALANCES",
    "CUSTOMERS",
    "VENDORS",
    "PROJECTS",
    "UNITS",
    "TRANSACTIONS",
    "BANK_TRANSACTIONS",
    "MATERIALS",
];
export const INTEGRATION_EVENTS = [
    "CUSTOMER_CREATED",
    "CUSTOMER_UPDATED",
    "BOOKING_CREATED",
    "BOOKING_UPDATED",
    "BOOKING_CANCELLED",
    "UNIT_ASSIGNED",
    "PAYMENT_RECEIVED",
    "DEMAND_RAISED",
];
//# sourceMappingURL=enums.js.map